import React from 'react'
import { DocumentNode } from 'graphql'
import {
  ApolloError,
  ApolloClient,
  ObservableQuery,
  ApolloCurrentResult,
  FetchPolicy
} from 'apollo-client'

export interface Variables {
  readonly [key: string]: any
}

interface QueryResult<TData> {
  readonly data?: TData
  readonly error?: ApolloError
  readonly loading: boolean
}

interface Props<TData> {
  readonly query: DocumentNode
  readonly variables?: Variables
  readonly dataKey: string
  readonly client: ApolloClient<any>
  readonly fetchPolicy?: FetchPolicy
  readonly areVariablesEqual?: (var1: Variables, var2: Variables) => boolean
  readonly children: (props: QueryResult<TData>) => React.ReactNode
}

interface State<TData> {
  readonly result: ApolloCurrentResult<TData>
}

export const areVariablesNotEqualCompareKeyVals = (
  var1: Variables,
  var2: Variables
) =>
  Object.keys(var1).some((key) => {
    if (Array.isArray(var1[key])) {
      return (
        !Array.isArray(var2[key]) || var1[key].join('') !== var2[key].join('')
      )
    }
    return var1[key] !== var2[key]
  })

export const areVariablesNotEqualCompareLengths = (
  var1: Variables,
  var2: Variables
) => Object.keys(var1).length !== Object.keys(var2).length

export const areVariablesNotEqual = (
  props: {
    readonly areVariablesEqual?: (var1: Variables, var2: Variables) => boolean
  },
  var1: Variables,
  var2: Variables
) =>
  areVariablesNotEqualCompareKeyVals(var1, var2) ||
  areVariablesNotEqualCompareLengths(var1, var2) ||
  (typeof props.areVariablesEqual !== 'undefined' &&
    !props.areVariablesEqual(var1, var2))

export class Query<TData> extends React.Component<Props<TData>, State<TData>> {
  private queryObservable: ObservableQuery<TData>
  private querySubscription

  constructor(props: Props<TData>) {
    super(props)
    const { query, variables, fetchPolicy = 'network-only' } = this.props
    this.queryObservable = this.props.client.watchQuery({
      query,
      variables,
      fetchPolicy
    })
    this.state = this.getCurrentData()
  }

  public componentDidMount() {
    this.startQuerySubscription()
  }

  public componentWillUnmount() {
    this.removeQuerySubscription()
  }

  public render() {
    const { children } = this.props
    const queryResult = this.getQueryResult()
    return children(queryResult)
  }

  public componentDidUpdate(prevProps: Props<TData>) {
    if (
      this.areVariablesNotEqual(
        prevProps.variables || {},
        this.props.variables || {}
      ) &&
      this.queryObservable
    ) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.queryObservable.setVariables(this.props.variables || {}, true)
    }
  }

  private areVariablesNotEqual = (var1, var2) =>
    areVariablesNotEqual(this.props, var1, var2)

  private getQueryResult = (): QueryResult<TData> => {
    const { dataKey } = this.props
    const {
      result: { loading, error, errors, data: raw }
    } = this.state

    const data = raw && raw[dataKey]
    return {
      data,
      loading,
      error: errors ? new ApolloError({ graphQLErrors: errors }) : error
    }
  }

  private startQuerySubscription = () => {
    if (!this.querySubscription && this.queryObservable) {
      const current = this.getQueryResult()
      this.querySubscription = this.queryObservable.subscribe({
        next: () => {
          if (!current || current.loading) {
            this.updateCurrentData()
          }
        },
        error: () => {
          this.updateCurrentData()
        }
      })
    }
  }

  private removeQuerySubscription = () => {
    if (this.querySubscription) {
      this.querySubscription.unsubscribe()
      delete this.querySubscription
    }
  }

  private updateCurrentData = () => {
    this.setState(this.getCurrentData())
  }

  private getCurrentData = () => {
    return {
      result: this.queryObservable.currentResult(),
      error: this.queryObservable.getLastError()
    }
  }
}
